import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MainCard from "../../components/cards/MainCard";
import ColorPicker from "../../components/ColorPicker";
import { getCardColor } from "../../utils/colorUtils";
import { useUpdatePhishReportThemeMutation } from "../../store/services/tenancy.service";

// The add-on's own defaults (gmailplugin src/Cards.js), shown as placeholders and in the preview.
const DEFAULTS = {
    colour: "#3DBE8B",
    name: "Phish Report",
    simulationText: {
        lead: "Congratulations",
        body: "on identifying a phishing campaign sent by Boxphish. Your continued vigilance is key in enhancing your organisation's cybersecurity defences."
    },
    reportedText: {
        lead: "Great work",
        body: "on reporting a potential phishing email promptly. Your swift action assists the organisation in remaining safe against cyber criminals."
    }
};

// Same limits as tenancy-service PhishReportThemeRules, which enforces them.
const MAX_NAME = 30;
const MAX_LEAD = 40;
const MAX_BODY = 300;

const isHex = (value) => /^#[0-9a-fA-F]{6}$/.test(value || "");

// WCAG contrast of white button text on the colour.
const contrastWithWhite = (hex) => {
    const channel = (i) => {
        const c = parseInt(hex.substr(i, 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
    return 1.05 / (luminance + 0.05);
};

const parseTheme = (json) => {
    try {
        return JSON.parse(json) || {};
    } catch {
        return {};
    }
};

function TextFields({ title, value, defaults, onChange }) {
    const text = value || { lead: "", body: "" };
    return (
        <Box mt={3}>
            <Typography variant="h5" mb={1}>{title}</Typography>
            <TextField
                fullWidth
                size="small"
                label="Highlighted phrase"
                placeholder={defaults.lead}
                inputProps={{ maxLength: MAX_LEAD }}
                value={text.lead}
                onChange={(e) => onChange({ ...text, lead: e.target.value })}
            />
            <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                sx={{ mt: 1 }}
                label="Rest of the message"
                placeholder={defaults.body}
                inputProps={{ maxLength: MAX_BODY }}
                value={text.body}
                onChange={(e) => onChange({ ...text, body: e.target.value })}
            />
        </Box>
    );
}

TextFields.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.object,
    defaults: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
};

// A rough Gmail side-panel card: Google Sans/Roboto, no shadows, the lead phrase in the colour.
function PreviewCard({ name, colour, logoUrl, text, showButton }) {
    return (
        <Box sx={{ width: 260, border: "1px solid #dadce0", borderRadius: 2, bgcolor: "#fff", color: "#202124", fontFamily: "Roboto, Arial, sans-serif", p: 2 }}>
            <Typography sx={{ fontFamily: "inherit", fontWeight: 500, mb: 1 }}>{name}</Typography>
            {logoUrl && <Box component="img" src={logoUrl} alt="" sx={{ maxWidth: "100%", maxHeight: 60, mb: 1 }} />}
            <Typography sx={{ fontFamily: "inherit", fontSize: 14 }}>
                <span style={{ color: colour }}>{text.lead}</span> {text.body}
            </Typography>
            {showButton &&
                <Box sx={{ mt: 2, bgcolor: colour, color: "#fff", borderRadius: 5, px: 2, py: 0.75, display: "inline-block", fontSize: 14 }}>
                    Phish Report
                </Box>
            }
        </Box>
    );
}

PreviewCard.propTypes = {
    name: PropTypes.string.isRequired,
    colour: PropTypes.string.isRequired,
    logoUrl: PropTypes.string,
    text: PropTypes.object.isRequired,
    showButton: PropTypes.bool
};

export default function PhishReportCustomisation({ data, logoUrl, onResult }) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const saved = parseTheme(data.phishReportTheme);
    // With no theme colour the add-on uses the portal's primary colour, then its own default.
    const baseColour = isHex(data.primaryHex) ? data.primaryHex : DEFAULTS.colour;

    const [colour, setColour] = useState(saved.colour || baseColour);
    const [name, setName] = useState(saved.name || "");
    const [simulationText, setSimulationText] = useState(saved.simulationText);
    const [reportedText, setReportedText] = useState(saved.reportedText);
    const [updateTheme, { isLoading }] = useUpdatePhishReportThemeMutation();

    const filled = (text) => (text?.lead?.trim() && text?.body?.trim() ? text : null);
    const previewColour = isHex(colour) ? colour : baseColour;
    const previewName = name.trim() || DEFAULTS.name;
    const lowContrast = isHex(colour) && contrastWithWhite(colour) < 4.5;

    const save = (themeToSave) => {
        updateTheme({ tenancyId: data.tenancyId, theme: themeToSave })
            .unwrap()
            .then(() => onResult(themeToSave ? "Gmail add-on theme saved." : "Gmail add-on theme reset to the defaults.", "success"))
            .catch((message) => onResult(message, "error"));
    };

    const handleSave = () => save({
        // Only store the colour when it differs from the portal's, so later portal changes still flow through.
        colour: colour === baseColour ? null : colour,
        name: name.trim() || null,
        simulationText: filled(simulationText),
        reportedText: filled(reportedText)
    });

    const handleReset = () => {
        setColour(baseColour);
        setName("");
        setSimulationText(null);
        setReportedText(null);
        save(null);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: getCardColor(lightMode) }}>
                    <Typography variant="h4">Gmail add-on</Typography>
                    <Typography variant="caption">
                        How the Phish Report Button looks in Gmail for your users. The logo comes from Branding → Theme.
                    </Typography>

                    <Box mt={3}>
                        <ColorPicker label="Colour" color={colour} onChange={setColour} />
                        {!isHex(colour) && <Alert severity="error">Use a hex colour like #7B3FE4.</Alert>}
                        {lowContrast && <Alert severity="warning">White button text is hard to read on this colour. Try a darker one.</Alert>}
                    </Box>

                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label="Name"
                        placeholder={DEFAULTS.name}
                        inputProps={{ maxLength: MAX_NAME }}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />

                    <TextFields title="After reporting a simulation" value={simulationText} defaults={DEFAULTS.simulationText} onChange={setSimulationText} />
                    <TextFields title="After reporting a real email" value={reportedText} defaults={DEFAULTS.reportedText} onChange={setReportedText} />

                    <Box mt={3} sx={{ display: "flex", gap: 1 }}>
                        <Button variant="contained" color="warning" disabled={isLoading || !isHex(colour)} onClick={handleSave}>
                            Save
                        </Button>
                        <Button variant="outlined" color="warning" disabled={isLoading} onClick={handleReset}>
                            Reset to default
                        </Button>
                    </Box>
                </MainCard>
            </Grid>

            <Grid item xs={12} md={6}>
                <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: getCardColor(lightMode) }}>
                    <Typography variant="h4" mb={2}>Preview</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <PreviewCard name={previewName} colour={previewColour} logoUrl={logoUrl}
                            text={{ lead: "Suspicious email?", body: "Report it to Boxphish and your security team." }} showButton />
                        <PreviewCard name={previewName} colour={previewColour} logoUrl={logoUrl}
                            text={filled(simulationText) || DEFAULTS.simulationText} />
                        <PreviewCard name={previewName} colour={previewColour} logoUrl={logoUrl}
                            text={filled(reportedText) || DEFAULTS.reportedText} />
                    </Box>
                </MainCard>
            </Grid>
        </Grid>
    );
}

PhishReportCustomisation.propTypes = {
    data: PropTypes.object.isRequired,
    logoUrl: PropTypes.string,
    onResult: PropTypes.func.isRequired
};
